import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FenceService } from '../../../../../../services/configurators-2d/applications/fence.service';
import { InstallationMixingStateService } from '../../../services/installation-mixing.state.service';
import {
  InstallationMixing
} from '../../../../../../models/configurators-2d/applications/fence/installations/installation-mixing';
import { MaterialsFacadeService } from '../../services/materials.facade.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { logError } from '../../../../../../services/helpers/sentry.helper';
import { Configuration } from '../../../../../../models/configurators-2d/applications/fence/configuration';
import { Installation } from '../../../../../../models/configurators-2d/applications/fence/installations/installation';
import { InstallationStateService } from '../../../services/installation.state.service';
import { getMaxHeight } from '../../../../../../services/helpers/fence-helper';
import { MaxBoardService } from '../../services/max-board.service';

@Component({
  selector: 'ng-casto-materials-installation-mixings',
  templateUrl: './materials-installation-mixings.component.html',
  styleUrl: './materials-installation-mixings.component.scss',
})
export class MaterialsInstallationMixingsComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly fenceService = inject(FenceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly installationStateService = inject(InstallationStateService);
  private readonly installationMixingStateService = inject(InstallationMixingStateService);
  private readonly materialsFacadeService = inject(MaterialsFacadeService);
  private readonly maxBoardService = inject(MaxBoardService);

  baseImgURL: string = undefined;

  /** Langue sélectionnée */
  currentLang: string;

  private get configuration(): Configuration {
    return this.materialsFacadeService.configuration();
  }

  /**
   * Un pose est un mixage si il est composé de deux poses
   * @protected
   */
  protected get isInstallationMixing(): boolean {
    return (
      this.configuration.configurationInstallations.length === 2 &&
      this.configuration.configurationInstallations.some(ci => ci.boardNumber >= 1)
    );
  }

  /**
   * Retourne la liste des mixages de pose disponibles
   * @protected
   */
  protected get installationMixings(): InstallationMixing[] {
    return this.installationMixingStateService.installationMixings();
  }

  protected get installationMixingCanBeReversed(): boolean {
    const installationMixing = this.installationMixingStateService.get(
      this.configuration.configurationInstallations[0].installationId,
      this.configuration.configurationInstallations[1].installationId
    );

    return installationMixing?.canBeReversed ?? false;
  }

  /**
   * Récupère la lame primaire
   * @protected
   */
  protected get secondaryInstallation(): Installation {
    const configurationInstallation = this.configuration.configurationInstallations.find(ci => ci.boardNumber);
    return this.installationStateService.getById(configurationInstallation.installationId);
  }

  /**
   * Vérifie dans quel sens est affiché le mixage des lames
   * @protected
   */
  get isInstallationMixAscOrder(): boolean {
    const primaryConfigurationInstallation = this.configuration.configurationInstallations.find(ci => ci.boardNumber);
    const secondaryConfigurationInstallation = this.configuration.configurationInstallations.find(
      ci => !ci.boardNumber
    );

    const primaryInstallationOrder = primaryConfigurationInstallation.order ?? 0;
    const secondaryInstallationOrder = secondaryConfigurationInstallation.order ?? 0;
    return primaryInstallationOrder < secondaryInstallationOrder;
  }

  get secondaryInstallationQuantity(): number {
    return this.configuration.configurationInstallations.find(ci => ci.boardNumber).boardNumber;
  }

  /**
   * Donne le nombre maximal de lames par pose mixte
   * @protected
   */
  protected get maxBoard(): number {
    return this.maxBoardService.maxBoardFromSecondaryInstallation(
      this.configuration.configurationInstallations,
      getMaxHeight(this.configuration)
    );
  }

  constructor() {
    this.baseImgURL = this.fenceService.apiUrl;
  }

  ngOnInit(): void {
    this.currentLang = this.translateService.getBrowserLang();
  }

  /**
   * Sélection de la pose mixte dans la configuration
   * @param installationMixing
   * @protected
   */
  protected selectInstallationMixing(installationMixing: InstallationMixing) {
    this.materialsFacadeService
      .selectInstallationMixing(installationMixing)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur sélection pose mixte:', err),
      });
  }

  /**
   * Est-ce que la pose mixte est sélectionnée ?
   * @param installationMixing
   * @protected
   */
  protected isInstallationMixingSelected(installationMixing: InstallationMixing) {
    return this.materialsFacadeService.isInstallationMixingSelected(installationMixing);
  }

  protected changeSecondaryInstallationQuantity(changeEvent: Event) {
    const domInput = changeEvent.target as HTMLInputElement;
    const newQuantity = Number.parseInt(domInput.value);

    if (Number.isNaN(newQuantity) || newQuantity <= 0 || newQuantity > this.maxBoard) {
      domInput.value = this.secondaryInstallationQuantity.toString();
      return;
    }

    this.materialsFacadeService
      .changeSecondaryInstallationQuantity(newQuantity)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur changement du nombre de lame de la pose:', err),
      });
  }

  /**
   * Change l'ordre d'affichage des poses
   */
  protected changeInstallationMixOrder(): void {
    this.materialsFacadeService
      .changeInstallationMixOrder()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur changement ordre poses:', err),
      });
  }

  /**
   * Créer la clé composite pour traquer les éléments du tableau des mixages de pose
   * @param installationMixing
   * @protected
   */
  protected getTrackKey(installationMixing: InstallationMixing): string {
    return (
      installationMixing.primaryInstallation.id.toString() + installationMixing.secondaryInstallation.id.toString()
    );
  }
}
